// utils/chatMessageCore.js
//
// The pure half of utils/chatMessage.js: grouping, date separators, URL
// detection and link-preview display text. No React / react-native import,
// so the web app (Vite) and node tests can load it. utils/chatMessage.js
// re-exports everything here, so mobile imports are unchanged.
//
// Grouping/threshold behavior MUST match the screens exactly; see the notes on
// GROUP_TIME_GAP_MS and getGroupInfo below.

// Consecutive messages from the same sender only stay grouped into one
// visual block if they're also within this long of each other — matches
// GroupMe's convention. Without this, two messages from the same person an
// hour apart still rendered as one continuous group with no separation.
export const GROUP_TIME_GAP_MS = 5 * 60 * 1000;

// Day-boundary label for a message's timestamp — "Today" / "Yesterday" / a
// weekday name within the last week / a "Mon 3"-style short date otherwise.
function getDateLabel(timestamp) {
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((todayStart - dateStart) / 86400000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: "long" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Firestore Timestamp, ISO string, or Date → millis, for the grouping
// time-gap check below. NaN-safe: an unparseable/invalid value collapses to 0
// so the gap subtraction never produces NaN comparisons.
export function toMillis(timestamp) {
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const t = date.getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Helper function to get two initials from full name.
export function getInitials(fullName) {
  if (!fullName) return "U";
  const names = fullName.trim().split(" ");
  if (names.length >= 2) {
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  }
  return fullName.substring(0, 2).toUpperCase() || "U";
}

// Detect URLs in text. Accepts (a) http(s):// links, (b) "www." links with
// no scheme, and (c) bare domains on a conservative TLD list
// ("instagram.com/ushr3d", "miami.edu"). The TLD list keeps "e.g." /
// "file.name" out. Mirrors firebase-functions/linkUnfurl.js FIRST_URL_RE
// (deliberately duplicated — separate Node project).
// No `i` flag on purpose: the bare-domain branch requires a LOWERCASE tld so
// "thank you.Me too" / "yes.Us" never read as links; scheme and www are
// matched case-insensitively inline instead.
const URL_RE =
  /([hH][tT][tT][pP][sS]?:\/\/[^\s<>"')\]]+|(?:^|(?<=[\s(:]))[wW][wW][wW]\.[^\s<>"')\]]+|(?:^|(?<=[\s(:]))(?:[a-z0-9-]+\.)+(?:co\.uk|org\.uk|ac\.uk|com\.au|com|org|edu|net|io|co|gov|app|dev|me|ly|gg|tv|us|info|ai|xyz|ee|gle|be|uk|ca|au|de|fr|es|it|nl|in|jp|kr|br|mx|nz)(?:\/[^\s<>"')\]]*)?(?=$|[\s.,;:!?)\]]))/g;
export function detectUrls(text) {
  if (!text) return [];
  return text.match(URL_RE) || [];
}

// Adds "https://" to a scheme-less link so Linking.openURL / the unfurl
// worker can use it. Leaves http(s) links untouched.
export function normalizeUrl(url) {
  const trimmed = (typeof url === "string" ? url.trim() : "").replace(/[.,;:!?)\]]+$/, "");
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

// ── KAN-96: link-preview-aware display text ─────────────────────────────────
//
// Mirrors firebase-functions/linkUnfurl.js's own TRAILING_PUNCTUATION_RE
// (deliberately duplicated, not imported — that file is a separate Node
// project this app doesn't/shouldn't depend on) so a URL detected in the
// middle of a sentence ("check this out: https://example.com.") compares
// equal to the punctuation-free URL that file actually stored as
// `link_preview.url` (its own extractFirstUrl trims the same trailing
// punctuation before fetching/persisting).
const TRAILING_PUNCTUATION_RE = /[.,;:!?)\]]+$/;

function stripTrailingPunctuation(url) {
  return typeof url === "string" ? url.replace(TRAILING_PUNCTUATION_RE, "") : "";
}

// Reduces a URL to a form that's stable across the specific differences a
// preview's stored URL and a user's actually-typed URL can disagree on
// without being "a different link": scheme (http vs https), a leading
// "www.", and a trailing slash. Falls back to a best-effort string
// normalization if `url` doesn't parse (RN/Hermes's built-in URL — already
// used elsewhere client-side, e.g. utils/linking.js — throws on a malformed
// or partial string rather than being lenient about it).
function normalizeUrlForCompare(rawUrl) {
  const trimmed = stripTrailingPunctuation(
    typeof rawUrl === "string" ? rawUrl.trim() : ""
  );
  if (!trimmed) return "";
  try {
    // normalizeUrl() first so a scheme-less "instagram.com" in the text and
    // the "https://instagram.com" the worker stored canonicalize the same way.
    const parsed = new URL(normalizeUrl(trimmed));
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    let path = parsed.pathname || "/";
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return `${host}${path}${parsed.search}`;
  } catch {
    return trimmed
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .replace(/\/+$/, "");
  }
}

// True when `a` and `b` refer to the "same" URL for link-preview purposes —
// tolerating a trailing slash, http-vs-https, and a "www." prefix
// difference, per KAN-96's product rule. Either input may be undefined/empty
// (no preview yet, or a message with no URL) — always false in that case,
// never a false-positive match.
export function urlsMatchForPreview(a, b) {
  const na = normalizeUrlForCompare(a);
  const nb = normalizeUrlForCompare(b);
  return !!na && na === nb;
}

// KAN-96 display rule, computed once per message so both the mention path
// and the plain-linkify path in MessageRow can share the same result:
//   - no preview yet (unfurl pending/failed, or no URL in the text at all):
//     show the raw text UNCHANGED — a message must never render blank (this
//     is the fix for the prior "link-only message renders blank" bug).
//   - the message text is ONLY the previewed URL (after trimming): hide the
//     text entirely — the preview card alone is the message.
//   - the message has other text plus the previewed URL somewhere in it:
//     strip exactly that URL occurrence (the first one that matches),
//     leaving any OTHER url in the text exactly as typed (still linkified
//     normally by renderTextWithLinks downstream).
//
// @param {string} text - the message's raw `message` field
// @param {string} [previewUrl] - `message.link_preview.url`, or falsy when
//   there is no preview (yet, or ever)
// @returns {{ hidden: boolean, text: string }}
export function getMessageDisplayText(text, previewUrl) {
  const safeText = typeof text === "string" ? text : "";
  if (!previewUrl) return { hidden: false, text: safeText };

  const urls = detectUrls(safeText);
  if (urls.length === 0) return { hidden: false, text: safeText };

  // URL-only message: the entire (trimmed) text IS the previewed URL.
  if (
    urls.length === 1 &&
    safeText.trim() === urls[0] &&
    urlsMatchForPreview(urls[0], previewUrl)
  ) {
    return { hidden: true, text: "" };
  }

  // Otherwise strip only the first occurrence that matches the previewed
  // URL — linkUnfurl.js only ever unfurls a message's FIRST URL (see its own
  // extractFirstUrl), so there is at most one occurrence to attribute to
  // this preview; every other URL in the text is left untouched.
  const matchIndex = urls.findIndex((u) => urlsMatchForPreview(u, previewUrl));
  if (matchIndex === -1) return { hidden: false, text: safeText };

  const matchedUrl = urls[matchIndex];
  const idx = safeText.indexOf(matchedUrl);
  if (idx === -1) return { hidden: false, text: safeText };

  const before = safeText.slice(0, idx);
  const after = safeText.slice(idx + matchedUrl.length);
  // Collapse the space left behind so removing the URL never leaves a
  // dangling double space or edge whitespace.
  const stripped = `${before}${after}`.replace(/[ \t]{2,}/g, " ").trim();

  return { hidden: stripped.length === 0, text: stripped };
}

// Message grouping (2a): determine whether the row at `index` in `messages` is
// the first / last of a consecutive same-sender run, and whether a date
// separator should precede it.
//
// Two messages group together only when ALL hold (exactly as the screens do
// today): same user_id, same day (getDateLabel equal / no date separator
// between them), the immediate neighbor is not a "system" row, and they're
// within GROUP_TIME_GAP_MS of each other. System neighbors always break the
// group (they're skipped exactly as today — the immediate-neighbor check
// treats a system row as a boundary rather than looking further past it).
export function getGroupInfo(messages, index) {
  const item = messages[index];
  const prevItem = index > 0 ? messages[index - 1] : null;
  const nextItem = index < messages.length - 1 ? messages[index + 1] : null;

  const currentDateLabel = getDateLabel(item.created_at);
  const prevDateLabel = prevItem ? getDateLabel(prevItem.created_at) : null;
  const showDateSeparator = currentDateLabel !== prevDateLabel;

  const samePrevSender =
    prevItem &&
    prevItem.message_type !== "system" &&
    prevItem.user_id === item.user_id &&
    !showDateSeparator &&
    toMillis(item.created_at) - toMillis(prevItem.created_at) <
      GROUP_TIME_GAP_MS;
  const sameNextSender =
    nextItem &&
    nextItem.message_type !== "system" &&
    nextItem.user_id === item.user_id &&
    getDateLabel(nextItem.created_at) === currentDateLabel &&
    toMillis(nextItem.created_at) - toMillis(item.created_at) <
      GROUP_TIME_GAP_MS;

  return {
    isFirstOfGroup: !samePrevSender,
    isLastOfGroup: !sameNextSender,
    showDateSeparator,
    dateLabel: currentDateLabel,
  };
}
